import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Search, Camera, ArrowRight, Globe, User } from 'lucide-react';

interface Instructor {
  id: string;
  business_name: string;
  bio: string | null;
  profile_photo_url: string | null;
  website_url: string | null;
  slug: string | null;
  workshop_count: number;
}

export default function Instructors() {
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadInstructors();
  }, []);

  const loadInstructors = async () => {
    try {
      // Get active leaders with their active listing count
      const { data: leaders, error } = await supabase
        .from('workshop_leaders')
        .select('id, business_name, bio, profile_photo_url, website_url, slug')
        .eq('is_active', true);

      if (error) throw error;

      if (!leaders || leaders.length === 0) {
        setInstructors([]);
        setLoading(false);
        return;
      }

      // Get active listing counts per leader
      const { data: listings } = await supabase
        .from('workshop_listings')
        .select('leader_id')
        .eq('is_active', true);

      const countMap = new Map<string, number>();
      (listings || []).forEach((l: { leader_id: string }) => {
        countMap.set(l.leader_id, (countMap.get(l.leader_id) || 0) + 1);
      });

      const enriched: Instructor[] = leaders.map(leader => ({
        ...leader,
        workshop_count: countMap.get(leader.id) || 0,
      }));

      // Sort by workshop count descending, then alphabetical
      enriched.sort((a, b) => b.workshop_count - a.workshop_count || a.business_name.localeCompare(b.business_name));

      setInstructors(enriched);
    } catch (err) {
      console.error('Error loading instructors:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return instructors;
    const q = searchQuery.toLowerCase();
    return instructors.filter(i =>
      i.business_name.toLowerCase().includes(q) ||
      i.bio?.toLowerCase().includes(q)
    );
  }, [instructors, searchQuery]);

  return (
    <>
      <SEOHead
        title="Astrophotography Instructors | Milky Way Planner"
        description="Meet the astrophotography workshop instructors on Milky Way Planner. Browse instructor profiles, bios, and upcoming workshops."
        canonicalUrl="/workshops/instructors"
      />
      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="bg-[#0f1923] text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
            <p className="text-gold uppercase text-sm font-semibold tracking-[0.14em] mb-4">
              Instructor Directory
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight mb-4 leading-[1.1]">
              Workshop Instructors
            </h1>
            <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              Learn from experienced astrophotographers who lead workshops at the best dark sky locations.
            </p>

            {/* Search */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search instructors..."
                className="pl-12 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/15 rounded-[4px]"
              />
            </div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          {/* Count */}
          <div className="flex items-center mb-8">
            <span className="text-sm text-muted-foreground">
              {filtered.length} instructor{filtered.length !== 1 ? 's' : ''}
            </span>

            <Link to="/workshops" className="ml-auto text-sm text-muted-foreground hover:text-foreground transition-colors">
              View all workshops →
            </Link>
          </div>

          {/* Instructor Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full bg-muted" />
                      <div className="space-y-2 flex-1">
                        <div className="h-4 bg-muted rounded w-3/4" />
                        <div className="h-3 bg-muted rounded w-1/2" />
                      </div>
                    </div>
                    <div className="h-12 bg-muted rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(instructor => (
                <Link
                  key={instructor.id}
                  to={instructor.slug ? `/workshops/instructors/${instructor.slug}` : `/workshops?q=${encodeURIComponent(instructor.business_name)}`}
                  className="group"
                >
                  <Card className="h-full hover:shadow-lg transition-shadow rounded-[4px]">
                    <CardContent className="p-6">
                      {/* Avatar + Name */}
                      <div className="flex items-center gap-4 mb-4">
                        {instructor.profile_photo_url ? (
                          <img
                            src={instructor.profile_photo_url}
                            alt={instructor.business_name}
                            className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-[#0f1923] flex items-center justify-center flex-shrink-0">
                            <User className="w-7 h-7 text-white/40" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <h3 className="font-serif font-bold text-foreground text-lg leading-tight group-hover:text-[#c9922a] transition-colors truncate">
                            {instructor.business_name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {instructor.workshop_count} workshop{instructor.workshop_count !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>

                      {/* Bio */}
                      {instructor.bio && (
                        <p className="text-sm text-muted-foreground line-clamp-3 mb-4 leading-relaxed">
                          {instructor.bio}
                        </p>
                      )}

                      {/* Website */}
                      {instructor.website_url && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70">
                          <Globe className="w-3 h-3" />
                          <span className="truncate">
                            {instructor.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Camera className="w-12 h-12 text-muted-foreground/20 mx-auto mb-6" />
              <h2 className="font-serif text-2xl font-bold text-foreground mb-3">
                {searchQuery ? 'No instructors match your search' : 'Instructors coming soon'}
              </h2>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto leading-relaxed">
                {searchQuery
                  ? 'Try adjusting your search.'
                  : 'We\'re building our instructor directory. Check back soon, or list your own workshops.'}
              </p>
              {searchQuery ? (
                <Button variant="outline" className="rounded-[4px]" onClick={() => setSearchQuery('')}>
                  Clear Search
                </Button>
              ) : (
                <Link to="/workshops/list-your-workshop">
                  <Button variant="premium" className="gap-2">
                    List Your Workshop <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              )}
            </div>
          )}

          {/* CTA */}
          {!loading && (
            <div className="mt-16 border-t border-border pt-16 text-center">
              <p className="text-gold-foreground uppercase text-sm font-semibold tracking-[0.14em] mb-4">
                For Instructors
              </p>
              <h2 className="font-serif text-2xl font-bold text-foreground mb-3 tracking-tight">
                Are you a workshop leader?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-lg mx-auto leading-relaxed">
                List your astrophotography workshops on Milky Way Planner and reach thousands of photographers planning their next shoot.
              </p>
              <Link to="/workshops/list-your-workshop">
                <Button variant="outline" size="lg" className="gap-2 rounded-[4px]">
                  List Your Workshop
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
